import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mail,
  ArrowLeft,
  Loader2,
  Send,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Logo from './ui/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Informe seu e-mail');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar solicitação');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row brand-canvas relative overflow-hidden">
      <div className="brand-glow brand-float" style={{ top: '-6rem', left: '-4rem', width: '28rem', height: '28rem', background: '#2563EB' }} />
      <div className="brand-glow brand-float" style={{ bottom: '-8rem', left: '20%', width: '32rem', height: '32rem', background: '#00D4CB', animationDelay: '2s' }} />

      <div className="relative z-10 flex flex-1 flex-col justify-between p-8 sm:p-12 lg:p-16">
        <Logo variant="full" theme="dark" showTagline iconClassName="h-10 w-auto" wordClassName="text-2xl" />

        <div className="max-w-xl py-12 lg:py-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-white/80 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#00D4CB]" />
            {sent ? 'E-mail Enviado' : 'Recuperação de Senha'}
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
            {sent ? 'Verifique seu e-mail' : 'Redefinir senha'}
            <br />
            <span className="brand-text-gradient">{sent ? 'instruções enviadas.' : 'sem estresse.'}</span>
          </h1>

          <p className="mt-6 max-w-md text-base sm:text-lg text-white/60">
            {sent
              ? 'Enviamos um link de redefinição para seu e-mail. Se a conta existir, você receberá as instruções em instantes.'
              : 'Digite seu e-mail cadastrado e enviaremos um link seguro para criar uma nova senha.'
            }
          </p>
        </div>

        <p className="hidden lg:block text-xs text-white/40">
          © {new Date().getFullYear()} GridProfile · Inteligência para Gestão de Pessoas
        </p>
      </div>

      <div className="relative z-10 w-full lg:w-[480px] xl:w-[520px] flex items-center justify-center p-6 sm:p-10 lg:p-12 bg-[#0B1220]/40 lg:bg-[#0B1220]/60 backdrop-blur-xl lg:border-l border-white/10">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-bold text-white">{sent ? 'E-mail Enviado' : 'Recuperar Senha'}</h2>
          <p className="mt-1 text-sm text-white/50">
            {sent
              ? 'Verifique sua caixa de entrada e spam'
              : 'Digite seu e-mail corporativo'
            }
          </p>

          {sent ? (
            <div className="mt-8 space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
              </div>
              <p className="text-white/60 text-sm text-center">
                Se o e-mail existir no sistema, você receberá um link de redefinição de senha.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full text-sm text-white/50 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Voltar para o login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">
                  E-mail corporativo
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu.email@empresa.com"
                    className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Enviar Link de Redefinição
                  </>
                )}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-white/50 hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                  Voltar para o login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
